"use client";
import { useEffect, useRef } from "react";
import anime from "animejs"; // v3.2.1
import { motion } from "framer-motion";
import {
  Users,
  Award,
  Lightbulb,
  Heart,
  UserCheck,
  PhoneCall,
  Briefcase,
  Search,
  Rocket,
} from "lucide-react";
import Header from "../../Components/Header";
import Footer from "../../Components/Footer";
import HowItWorks from "../../Components/HowItWorks";

export default function AboutPage() {
  // -------- data
  const workSteps = [
    {
      icon: UserCheck,
      title: "Profile Analysis",
      desc: "We audit your Job Profile & Resume to surface strengths and gaps.",
    },
    {
      icon: PhoneCall,
      title: "1:1 Guidance Call",
      desc: "A mentor aligns your story, brand, and goals — step by step.",
    },
    {
      icon: Briefcase,
      title: "Mock Interviews",
      desc: "Role-specific drills to build confidence and fix weak areas.",
    },
    {
      icon: Rocket,
      title: "Real Opportunities",
      desc: "Warm connects to startups & MNCs — TCS, Wipro, Infosys & more.",
    },
    {
      icon: Search,
      title: "Continuous Improvement",
      desc: "We iterate with targeted actions until you land stronger results.",
    },
  ];

  // -------- refs
  const heroRef = useRef(null);
  const spotlightRef = useRef(null);
  const growRef = useRef(null);
  const stepRefs = useRef([]);
  const svgRef = useRef(null);
  const valuesRef = useRef([]);

  useEffect(() => {
    // HERO: spotlight follows cursor
    const hero = heroRef.current;
    const glow = spotlightRef.current;
    if (hero && glow) {
      const onMove = (e) => {
        const r = hero.getBoundingClientRect();
        const x = e.clientX - r.left;
        const y = e.clientY - r.top;
        glow.style.background = `radial-gradient(600px circle at ${x}px ${y}px, rgba(255,255,255,0.08), transparent 40%)`;
      };
      hero.addEventListener("mousemove", onMove);

      // floating blobs
      anime({
        targets: hero.querySelectorAll("[data-blob]"),
        translateY: [0, 18],
        direction: "alternate",
        loop: true,
        easing: "easeInOutSine",
        duration: 3600,
        delay: anime.stagger(260),
      });

      // headline word-by-word
      const words = hero.querySelectorAll("[data-hero-word]");
      anime
        .timeline({ easing: "easeOutQuad" })
        .add({
          targets: hero.querySelector("[data-hero-chip]"),
          opacity: [0, 1],
          translateY: [10, 0],
          duration: 350,
        })
        .add(
          {
            targets: words,
            opacity: [0, 1],
            translateY: [20, 0],
            delay: anime.stagger(50),
            duration: 550,
          },
          "-=100"
        )
        .add(
          {
            targets: hero.querySelector("[data-hero-sub]"),
            opacity: [0, 1],
            translateY: [10, 0],
            duration: 450,
          },
          "-=200"
        );

      return () => hero.removeEventListener("mousemove", onMove);
    }
  }, []);

  useEffect(() => {
    // GROW: stepper reveal + path draw when in view
    const growEl = growRef.current;
    if (!growEl) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;

          // draw path
          const path = svgRef.current?.querySelector("path");
          if (path) {
            const len = path.getTotalLength();
            path.style.strokeDasharray = `${len}`;
            path.style.strokeDashoffset = `${len}`;
            anime({
              targets: path,
              strokeDashoffset: [len, 0],
              duration: 1200,
              easing: "easeInOutCubic",
            });
          }

          // steps
          anime({
            targets: stepRefs.current,
            opacity: [0, 1],
            translateY: [30, 0],
            scale: [0.98, 1],
            delay: anime.stagger(120),
            duration: 700,
            easing: "easeOutBack",
          });

          obs.disconnect();
        });
      },
      { threshold: 0.3 }
    );

    obs.observe(growEl);
    return () => obs.disconnect();
  }, []);

  // 3D tilt micro-interaction
  const onCardMove = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const cx = e.clientX - r.left;
    const cy = e.clientY - r.top;
    const rx = ((cy - r.height / 2) / (r.height / 2)) * -6;
    const ry = ((cx - r.width / 2) / (r.width / 2)) * 6;
    anime({
      targets: el,
      rotateX: rx,
      rotateY: ry,
      duration: 180,
      easing: "easeOutQuad",
    });
  };
  const onCardLeave = (e) =>
    anime({
      targets: e.currentTarget,
      rotateX: 0,
      rotateY: 0,
      duration: 260,
      easing: "easeOutQuad",
    });

  // Magnetic small buttons
  const onMagnetMove = (e) => {
    const btn = e.currentTarget;
    const r = btn.getBoundingClientRect();
    const mx = e.clientX - r.left - r.width / 2;
    const my = e.clientY - r.top - r.height / 2;
    anime({
      targets: btn,
      translateX: mx * 0.08,
      translateY: my * 0.08,
      duration: 160,
      easing: "easeOutQuad",
    });
  };
  const onMagnetLeave = (e) =>
    anime({
      targets: e.currentTarget,
      translateX: 0,
      translateY: 0,
      duration: 180,
      easing: "easeOutQuad",
    });

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen">
      <Header />

      {/* ========================= HERO ========================= */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-28"
      >
        {/* cursor spotlight */}
        <div
          ref={spotlightRef}
          className="pointer-events-none absolute inset-0"
        />
        {/* blobs */}
        <div
          data-blob
          className="absolute -top-12 -right-10 h-64 w-64 rounded-full bg-white/15 blur-3xl"
        />
        <div
          data-blob
          className="absolute -bottom-16 -left-12 h-72 w-72 rounded-full bg-fuchsia-300/20 blur-3xl"
        />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <span
            data-hero-chip
            className="inline-block text-[11px] uppercase tracking-widest font-semibold bg-white/15 rounded-full px-3 py-1"
          >
            Craft • Optimize • Grow
          </span>
          <h1 className="text-5xl md:text-6xl font-extrabold mt-4 leading-tight">
            {splitWords(
              "About ProfileWise — We turn potential into opportunity"
            ).map((w, i) => (
              <span
                key={i}
                data-hero-word
                className="inline-block opacity-0 mr-2"
              >
                {w}
              </span>
            ))}
          </h1>
          <p data-hero-sub className="opacity-0 text-lg text-indigo-100 mt-4">
            Resume reviews, job portal improvements, real industry referrals,
            mock interviews, and real-time support via call and email — designed
            to boost your career.
          </p>
        </div>
      </section>

      {/* ========================= WHO WE ARE ========================= */}
      <section className="py-18 px-6 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <h2 className="text-4xl font-bold mb-4">Who We Are</h2>
          <p className="text-gray-600 leading-relaxed">
            We’re a team of strategists, writers, and mentors. From ATS-ready
            resumes to recruiter-magnet LinkedIn profiles, we position you to be
            discovered — and remembered.
          </p>
        </motion.div>

        {/* mission */}
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1200&q=80"
            alt="Team collaboration"
            className="rounded-3xl shadow-2xl"
          />
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-semibold mb-4 text-indigo-600">
              Our Philosophy
            </h3>
            <p className="text-gray-600 mb-4">
              Every career is a story. We combine creativity, data, and
              mentorship so your story lands with the right audience.
            </p>
            <p className="text-gray-600">
              Fresher or seasoned, we help you build a brand that attracts
              opportunities and boosts confidence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ========================= HOW WE HELP YOU GROW ========================= */}
      <section ref={growRef} className="py-24 bg-white">
        <h2 className="text-4xl font-bold text-center mb-4 text-indigo-700">
          How We Help You Grow
        </h2>
        <p className="text-center text-gray-600 mb-14">
          A guided path from audit → practice → opportunities → iteration.
        </p>

        <div className="relative max-w-7xl mx-auto px-6">
          {/* Progress line */}
          <div className="absolute left-0 right-0 top-24 hidden lg:block">
            <svg ref={svgRef} viewBox="0 0 1000 120" className="w-full h-28">
              <path
                d="M40,80 C200,10 300,120 500,60 C700,0 820,120 960,40"
                fill="none"
                stroke="url(#grad)"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="grad" x1="0" x2="1">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#a855f7" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-7 relative z-10">
            {workSteps.map((s, i) => (
              <div
                key={s.title}
                ref={(el) => (stepRefs.current[i] = el)}
                onMouseMove={onCardMove}
                onMouseLeave={onCardLeave}
                className="group relative bg-white/90 backdrop-blur border border-indigo-100 rounded-2xl p-6 text-center shadow-lg hover:shadow-2xl transition will-change-transform"
                style={{ transformStyle: "preserve-3d" }}
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white rounded-full w-9 h-9 flex items-center justify-center text-xs font-bold shadow-md">
                  {i + 1}
                </div>
                <div className="flex justify-center mb-4">
                  <s.icon className="w-10 h-10 text-indigo-600" />
                </div>
                <h4 className="font-semibold text-lg mb-2 text-gray-900">
                  {s.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {s.desc}
                </p>

                {/* <button
                  onMouseMove={onMagnetMove}
                  onMouseLeave={onMagnetLeave}
                  className="mt-4 inline-flex items-center gap-2 text-[12px] font-semibold px-3 py-1.5 rounded-full border border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  Learn more →
                </button> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================= VALUES ========================= */}
      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8">
          {[
            {
              icon: Users,
              title: "Community First",
              desc: "We empower professionals through practical guidance & collaboration.",
            },
            {
              icon: Lightbulb,
              title: "Innovation",
              desc: "Creative + data-driven methods to accelerate growth.",
            },
            {
              icon: Award,
              title: "Excellence",
              desc: "Quality mentorship and measurable outcomes.",
            },
            {
              icon: Heart,
              title: "Integrity",
              desc: "Trust built on clarity and consistency.",
            },
          ].map((it, idx) => (
            <div
              key={it.title}
              ref={(el) => (valuesRef.current[idx] = el)}
              onMouseMove={onCardMove}
              onMouseLeave={onCardLeave}
              className="bg-white/90 backdrop-blur border border-indigo-100 rounded-2xl p-8 text-center shadow-lg hover:shadow-2xl transition will-change-transform"
              style={{ transformStyle: "preserve-3d" }}
            >
              <it.icon className="w-10 h-10 text-indigo-600 mx-auto mb-3" />
              <h4 className="font-semibold text-lg mb-2">{it.title}</h4>
              <p className="text-gray-600">{it.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

/** Utility: split a headline into words to animate with anime.js */
function splitWords(str) {
  return str.split(" ").map((w) => w + " ");
}
